import AppRoutes from "../routing/routes";
import SessionManager from "clientside-session-manager";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import AppStore from "./@redux/store";
import { v4 as uuidv4 } from "uuid";
import { showSnackBar } from "./@redux/snackbar";
import { setVideos } from "./@redux/videos";
import axios from "axios";
import jwt_decode from "jwt-decode";
import moment from "moment-timezone";
import { exportContentsToExcel } from "./export.util";

export const isProduction = () =>
  `${process.env.REACT_APP_ENV}` === "production";

export const isStaging = () => `${process.env.REACT_APP_ENV}` === "staging";

export const isDev = () => `${process.env.REACT_APP_ENV}` === "development";

export const paginationOption = [10, 25, 50, 100, 200, 500];

const authTag = "authenticated",
  sessionTimeout = !isDev ? 30 : 86400,
  tempUid = "_uid";

export const refreshTimeout = 4000; //milliseconds

export const paymentTimeout = 75; //seconds

export const filesUrl = "";

export const acceptedMimes = "image/jpg,image/png";

export const maxFileSize = 2097152;

export const InitSessionObserver = () => {
  const location = useLocation();
  useEffect(() => {
    if (window.location.pathname.includes(AppRoutes.dashboard)) {
      SessionManager.updateToken(`token-${new Date().getTime()}`);
      var timeoutId = parseInt(sessionStorage.getItem("timeOut") || "-1");
      clearTimeout(parseInt(timeoutId));
      timeoutId = setTimeout(() => logout(), sessionTimeout * 1000 * 60);
      sessionStorage.setItem("timeOut", String(timeoutId));
    }
  }, [location]);
};

export const generateUniqueId = () => uuidv4();

export const initApp = () => {
  if (!localStorage.getItem(tempUid)) {
    localStorage.setItem(tempUid, generateUniqueId());
  }
};

export const getHandlerPathProps = (payload) => {
  const parts = payload.path.split("/");
  return {
    path: parts[0],
    event: parts[parts.length - 1].split("?")[0],
    returnPath: `${payload.nodeId}/${payload.path}`,
  };
};

export const watchLiveData = (queue, event, callback) => {
  queue.subscribe({ noAck: false }, (msg) => {
    const { name, data } = JSON.parse(msg.bodyToString());
    if (name === event) callback(data);
    msg.ack();
  });
};

export const navigate = (path, dashboard = false) => {
  AppStore.getState().navigator.navigate(
    `${dashboard ? AppRoutes.dashboard : AppRoutes.auth}/${path}`,
  );
};

export const SetVideos = (videos) => {
  AppStore.dispatch(setVideos(videos));
};

export const getVideos = () => {
  return AppStore.getState().videos.instance;
};

export const loggedInitSession = (response) => {
  const data = jwt_decode(response.data).data;
  data.role = parseInt(`${data.role}`);
  if ([1, 2, 7].includes(data.role) && data.permissions.length) {
    SessionManager.create(
      `token-${new Date().getTime()}`,
      { ...data, authenticated: true, "auth-token": response.data },
      sessionTimeout,
    );
    updateAddSessionValue(authTag, true);
    navigate(AppRoutes.contents, true);
  } else {
    logout();
  }
};

export const getParams = () => {
  if (window.location.search) {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const objs = Object.fromEntries(urlSearchParams.entries());
    var _objs = {};
    for (const key in objs) {
      _objs[`${key}`] =
        objs[key] === "true" || objs[key] === "false"
          ? JSON.parse(objs[key])
          : objs[key];
    }
    return _objs;
  } else {
    return {};
  }
};

export const allowedOperations = () => {
  const path = window.location.pathname;
  const { actions, permissions } = getActiveUser() || {
    actions: [],
    permissions: [],
  };
  const hasPermission =
    (permissions.length && permissions.find((perm) => perm === "*")) ||
    permissions.some((perm) => path.toLowerCase().includes(perm.toLowerCase()));
  return {
    canEdit: actions.includes("PATCH") && hasPermission,
    canDelete: actions.includes("DELETE") && hasPermission,
    canCreate: actions.includes("POST") && hasPermission,
  };
};

export const guardRoute = (recover = false, allowed = true) => {
  if (recover && recover === true) {
    if (!getParams().code) navigate(AppRoutes.login);
  } else {
    const valid = SessionManager.exists(),
      path = window.location.pathname,
      deepLink = path.includes(".well-known"),
      navToLogin =
        !valid &&
        ((!path.includes(AppRoutes.login) &&
          !path.includes(AppRoutes.forgot) &&
          !path.includes(AppRoutes.recover)) ||
          !allowed);

    // 1. Not logged in → redirect to login
    if (navToLogin && !deepLink) {
      window.location.href = `${AppRoutes.auth}/${AppRoutes.login}`;
      return;
    }

    const user = getActiveUser();
    const role = user?.role;
    const permissions = [
      ...(user?.permissions || []),
      "Auth",
      "reset-password",
    ];

    if (!user && !path.toLowerCase().includes("login")) {
      navigate(AppRoutes.login, true);
      return;
    }

    const isAdmin = role === 1;
    const inDashboard = path.toLowerCase().includes("dashboard");
    const hasPermission =
      (permissions.length && permissions.find((perm) => perm === "*")) ||
      permissions.some((perm) =>
        path.toLowerCase().includes(perm.toLowerCase()),
      );

    if (!isAdmin) {
      if (!inDashboard) {
        if (hasPermission && permissions.length > 1) {
          navigate(`${permissions[0].toLowerCase()}`, true);
          return;
        } else {
          navigate(AppRoutes.login, true);
          return;
        }
      } else if (inDashboard) {
        if (!hasPermission) {
          navigate(`${permissions[0].toLowerCase()}`, true);
          return;
        } else {
        }
      }
    }
  }
};

export const updateAddSessionValue = (key, value, update = false) => {
  if (update) {
    SessionManager.updateDetail(key, value);
  } else {
    SessionManager.addDetail(key, value);
  }
};

export const logout = () => {
  SessionManager.destroy();
  localStorage.removeItem(tempUid);
  initApp();
  window.location.href = `${AppRoutes.auth}/${AppRoutes.login}`;
};

export const getActiveUser = () => {
  return SessionManager.getAll();
};

export const getUserRole = (role) => {
  return role === 1
    ? "Admin"
    : role === 2
      ? "Editor"
      : role === 7
        ? "Stakeholder"
        : "Guest";
};

export const UserRoles = {
  system: 0,
  admin: 1,
  editor: 2,
};

export const getUserStatus = (status) => {
  return {
    isLabel: true,
    value: status === true ? "ACTIVE" : "INACTIVE",
    color: status === true ? "success" : "error",
  };
};

export const getNotificationStatus = (status) => {
  return {
    isLabel: true,
    value: status === true ? "SENT" : "SCHEDULED",
    color: status === true ? "success" : "info",
  };
};

export const purpose = (status) => {
  return {
    isLabel: true,
    value: status ? "YES" : "NO",
    color: status ? "success" : "error",
  };
};

export const scheduled = () => {
  return {
    isLabel: true,
    value: "SCHEDULED",
    color: "info",
  };
};

export const getAvatarLabel = (objects, mKey) => {
  var keys = Object.keys(objects),
    label = null;
  for (let step = 0; step < keys.length; step++) {
    if (keys[step].includes(mKey) && objects[keys[step]]) {
      label = objects[keys[step]];
    }
  }
  if (label) {
    return `${label.split(" ")[0]}`.substring(0, 1).toUpperCase();
  }
  return ".";
};

export const validateName = (value) => {
  const nameRegex = /^[a-zA-Z]+(?:[\s'-][a-zA-Z]+)*$/;
  return nameRegex.test(value);
};

export const validateDomain = (value) => {
  const domainRegex =
    /^(?!www\.)(?!https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
  return domainRegex.test(value);
};

export const validateEmail = (value) => {
  return String(value)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

export const showSnack = (message) => {
  AppStore.dispatch(showSnackBar({ message: message }));
};

export const isError = (field) => {
  return field.error && field.touched;
};

export const cleanObject = (data) => {
  Object.keys(data).forEach((key) => {
    if (typeof data[key] === "string" && data[key].trim().length <= 0) {
      delete data[key];
    }
  });
  return data;
};

export const validateFields = (fields) => {
  var validated = [];
  Object.keys(fields).forEach((key) => {
    validated.push(!fields[key].error && fields[key].touched);
  });
  return validated.filter((entry) => !entry).length === 0;
};

export const getStatus = (status, plain = false) => {
  const success =
    status === "COMPLETED" ||
    status === "COMPLETE" ||
    status === "Authenticated" ||
    status === "Active";
  const pending =
    status === "PENDING" ||
    status === "PROCESSING" ||
    `${status}`.startsWith("STREAMING") ||
    `${status}`.startsWith("YOUTUBE");
  const failed = status === "ERROR" || status === "Needs Auth" || "Expired";
  const info =
    status === "FINALIZING" ||
    `${status}`.startsWith("VIDEO") ||
    (!success && !pending && !failed);

  const color = info
    ? "info"
    : success
      ? "success"
      : pending
        ? "warning"
        : failed
          ? "error"
          : "info";
  return {
    isLabel: true,
    value: `${status}`.toUpperCase(),
    color: color,
    icon: success
      ? plain
        ? "check"
        : "check_circle"
      : pending
        ? plain
          ? "query_builder"
          : "query_builder"
        : failed
          ? plain
            ? "close"
            : "highlight_off"
          : "error_outline",
  };
};

export const getHeaders = (requestId) => {
  const token = SessionManager.getDetail("auth-token");
  if (!token) return null;
  return {
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
      "source-client": "uploader-app",
      "x-request-id": requestId || generateUniqueId(),
    },
  };
};

export const downloadContents = (label) => {
  exportContentsToExcel(SessionManager.getDetail("auth-token"), label);
};

export const sendGetRequest = async (
  endPoint,
  onLoading,
  onResponse,
  requestId,
) => {
  if (onLoading) onLoading(true);
  return new Promise((resolve) => {
    axios
      .get(
        `${process.env.REACT_APP_BASE_URL}${endPoint}`,
        getHeaders(requestId),
      )
      .then((response) => {
        if (onLoading) onLoading(false);
        if (onResponse) onResponse(response.data.data);
        resolve(response.data.data);
      })
      .catch((error) => {
        console.error(error);
        if (onLoading) onLoading(false);
        if (onResponse) onResponse(null);
        resolve(null);
      });
  });
};

export const getStreamingReport = async (onLoading, onResponse) => {
  if (onLoading) onLoading(true);
  return new Promise((resolve) => {
    axios
      .get(
        `${process.env.REACT_APP_STREAM_GENERATOR_URL}/tasks/report?env=${
          isProduction() ? "production" : "development"
        }`,
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_STREAM_GENERATOR_TOKEN}`,
          },
        },
      )
      .then((response) => {
        if (onLoading) onLoading(false);
        if (onResponse) onResponse(response.data.data);
        resolve(response.data.data);
      })
      .catch((error) => {
        console.error(error);
        if (onLoading) onLoading(false);
        if (onResponse) onResponse(null);
        resolve(null);
      });
  });
};

export const sendPostRequest = (
  endPoint,
  data,
  onLoading,
  onResponse,
  requestId,
  onUploading,
) => {
  if (onLoading) onLoading(true);
  let configs = getHeaders(requestId);
  if (data.files) {
    configs.headers = {
      ...configs.headers,
      "Content-Type": "multipart/form-data",
    };

    configs = {
      ...configs,
      onUploadProgress: (progressEvent) => {
        const progress = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        if (onUploading) onUploading(true, progress);
        console.log(`In-app Progress: Uploading: ${progress}%`);
      },
    };
  }
  return new Promise((resolve) => {
    axios
      .post(`${process.env.REACT_APP_BASE_URL}${endPoint}`, data, configs)
      .then((response) => {
        if (onLoading) onLoading(false);
        if (onResponse) onResponse(response.data);
        resolve(response.data);
      })
      .catch((error) => {
        console.error(error);
        if (onLoading) onLoading(false);
        resolve(null);
        if (onResponse) onResponse(error.response.data);
      });
  });
};

export const sendPatchRequest = (
  endPoint,
  data,
  onLoading,
  onResponse,
  requestId,
) => {
  if (onLoading) onLoading(true);
  let configs = getHeaders(requestId);
  if (data.files) {
    configs.headers = {
      ...configs.headers,
      "Content-Type": "multipart/form-data",
    };
  }
  return new Promise((resolve) => {
    axios
      .patch(`${process.env.REACT_APP_BASE_URL}${endPoint}`, data, configs)
      .then((response) => {
        if (onLoading) onLoading(false);
        if (onResponse) onResponse(response.data);
        resolve(response.data);
      })
      .catch((error) => {
        if (onLoading) onLoading(false);
        resolve(null);
        if (onResponse) onResponse(error.response.data);
      });
  });
};

export const sendDeleteRequest = async (
  endPoint,
  uuid,
  onLoading,
  onResponse,
  requestId,
) => {
  if (onLoading) onLoading(true);
  return new Promise((resolve) => {
    axios
      .delete(
        `${process.env.REACT_APP_BASE_URL}${endPoint}/${uuid}`,
        getHeaders(requestId),
      )
      .then((response) => {
        if (onLoading) onLoading(false);
        if (onResponse) onResponse(response.data);
        resolve(response.data);
      })
      .catch((error) => {
        if (onLoading) onLoading(false);
        resolve(null);
        if (onResponse) onResponse(error.response.data);
      });
  });
};

export const formatDate = (dateTime, format = "MMM DD, YYYY") => {
  return moment(dateTime).format(format);
};

export const toTzTime = (dateTime) => {
  return moment(dateTime).tz("Africa/Dar_es_Salaam");
};

export const toTzTimeFormat = (dateTime, format = "MMM DD, YYYY") => {
  return moment(dateTime).tz("Africa/Dar_es_Salaam").format(format);
};

export const getDateInDays = (days) => {
  return moment().subtract(days, "days").toDate();
};

export const getDaysAfter = (days) => {
  return moment().add(days, "days").toDate();
};

export const getFolderName = (folder, capitalize = true) => {
  const parts = folder.split("/");
  if (parts.length === 1) return folder;
  const name = parts[parts.length - 2];
  return capitalize ? name.charAt(0).toUpperCase() + name.slice(1) : name;
};

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getDisplayFolderName = (name) => {
  const newName = name
    .replace("_", " ")
    .replace("%21", "!")
    .replace("%27", "'")
    .replace("%28", "(")
    .replace("%29", ")")
    .replace("%2C", ",")
    .replace("%20", " ")
    .replace("%2F", " ")
    .substring(0, 20);
  return `${name.length <= 20 ? name : newName}${
    name.length <= 20 ? "" : "..."
  }`;
};

export const removeEmpty = (_object) => {
  const clean = (obj) => {
    if (Array.isArray(obj)) {
      return obj
        .map((v) =>
          v && typeof v === "object" && Object.keys(v).length ? clean(v) : v,
        )
        .filter((v) => !(v == null))
        .filter((_obj) => Object.keys(_obj).length > 0);
    } else {
      const _obj = Object.entries(obj)
        .map(([k, v]) => [
          k,
          v && typeof v === "object" && Object.keys(v).length ? clean(v) : v,
        ])
        .reduce(
          (a, [k, v]) =>
            v === null || v === "null" || v === undefined ? a : ((a[k] = v), a),
          {},
        );

      return Object.keys(_obj).length ? _obj : null;
    }
  };
  return clean(_object);
};

export const shortenUrl = (url) => {
  const parts = url.split("/");
  const shortened = `${parts[0]}//${parts[2]}/.../${parts
    .slice(parts.length - 1)
    .join("/")}`;
  return shortened;
};

export const getDayOfWeek = () => {
  return new Date().toLocaleDateString("en-US", { weekday: "long" });
};
